import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Star, MapPin, Clock, Coins, Users, User, Check, ArrowLeft } from "lucide-react";
import { get } from "@/lib/api";
import { CATEGORY_LABELS, formatDate } from "@/lib/data";
import { SessionCard } from "@/components/consumer/SessionCard";

export default function StudioDetail() {
  const { id } = useParams();
  const { data: studio, isLoading } = useQuery({ queryKey: ["studio", id], queryFn: () => get(`/studios/${id}`) });
  const [active, setActive] = useState(0);

  if (isLoading || !studio) return <div className="mx-auto max-w-7xl px-5 py-16 text-[#5C5856]">Yükleniyor…</div>;

  return (
    <div className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
      <Link to="/kesfet" className="mb-5 inline-flex items-center gap-1.5 text-sm text-[#5C5856] hover:text-[#2D2926]">
        <ArrowLeft className="h-4 w-4" /> Keşfet'e dön
      </Link>

      {/* Gallery */}
      <div className="grid gap-3 lg:grid-cols-[1fr_360px]">
        <div className="relative overflow-hidden rounded-2xl">
          <img src={studio.images[active]} alt={studio.name} className="h-[300px] w-full object-cover lg:h-[440px]" />
        </div>
        <div className="grid grid-cols-4 gap-3 lg:grid-cols-2">
          {studio.images.map((img, i) => (
            <button
              key={i}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setActive(i)}
              className={`overflow-hidden rounded-xl border-2 transition-all ${active === i ? "border-[#8C6A5D]" : "border-transparent opacity-80 hover:opacity-100"}`}
            >
              <img src={img} alt="" className="h-20 w-full object-cover lg:h-[105px]" />
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_320px]">
        <div>
          <span className="text-sm font-medium uppercase tracking-wide text-[#8C6A5D]">{CATEGORY_LABELS[studio.category]}</span>
          <h1 className="mt-1 font-display text-4xl font-semibold tracking-tight sm:text-5xl">{studio.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-[#5C5856]">
            <span className="flex items-center gap-1.5"><Star className="h-4 w-4 fill-[#8C6A5D] text-[#8C6A5D]" /> {studio.rating} · {studio.review_count} değerlendirme</span>
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {studio.address}</span>
            <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> Kapasite {studio.capacity} kişi</span>
          </div>

          <p className="mt-6 max-w-2xl leading-relaxed text-[#5C5856]">{studio.description}</p>

          {/* Services */}
          <h2 className="mt-10 font-display text-2xl font-semibold">Sunulan hizmetler</h2>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {studio.services.map((s) => (
              <span key={s} className="flex items-center gap-1.5 rounded-full border border-[#E5E0D8] bg-white px-4 py-2 text-sm">
                <Check className="h-3.5 w-3.5 text-[#4A5D4E]" /> {s}
              </span>
            ))}
          </div>

          {/* Hours */}
          <h2 className="mt-10 font-display text-2xl font-semibold">Çalışma saatleri</h2>
          <div className="mt-4 max-w-md divide-y divide-[#F0EAE0] rounded-2xl border border-[#E5E0D8] bg-white">
            {Object.entries(studio.hours).map(([day, hrs]) => (
              <div key={day} className="flex items-center justify-between px-5 py-3 text-sm">
                <span className="text-[#5C5856]">{day}</span>
                <span className="font-medium">{hrs}</span>
              </div>
            ))}
          </div>

          {/* Upcoming sessions */}
          <h2 className="mt-10 font-display text-2xl font-semibold">Yaklaşan seanslar</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {studio.sessions.slice(0, 8).map((s, i) => <SessionCard key={s.id} session={s} index={i} />)}
          </div>
        </div>

        {/* Sticky booking card */}
        <aside className="h-fit lg:sticky lg:top-24">
          <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6">
            <div className="flex items-baseline gap-1.5">
              <Coins className="h-5 w-5 text-[#8C6A5D]" />
              <span className="font-display text-4xl font-semibold text-[#8C6A5D]">{studio.credit_price}</span>
              <span className="text-[#5C5856]">kredi / seans</span>
            </div>
            <p className="mt-2 text-sm text-[#5C5856]">Eğitmenler ve müsait saatler için bir seans seçin.</p>
            {studio.sessions[0] && (
              <div className="mt-5 rounded-xl bg-[#F4EFE7] p-4">
                <p className="text-xs uppercase tracking-wide text-[#8C6A5D]">En yakın seans</p>
                <p className="mt-1 font-medium">{studio.sessions[0].title}</p>
                <p className="mt-0.5 flex items-center gap-1.5 text-sm text-[#5C5856]">
                  <Clock className="h-3.5 w-3.5" /> {formatDate(studio.sessions[0].date)} · {studio.sessions[0].start_time}
                </p>
                <p className="mt-0.5 flex items-center gap-1.5 text-sm text-[#5C5856]"><User className="h-3.5 w-3.5" /> {studio.sessions[0].instructor}</p>
              </div>
            )}
            {studio.sessions[0] && (
              <Link
                to={`/seans/${studio.sessions[0].id}`}
                data-testid="studio-book-nearest-button"
                className="mt-5 block rounded-full bg-[#8C6A5D] py-3 text-center font-medium text-white transition-colors hover:bg-[#705448]"
              >
                Seansı görüntüle
              </Link>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
